import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import staticConfig from '../static-config.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATIC_MODE = process.env.REACT_APP_STATIC_MODE === 'true';
export const API = `${BACKEND_URL || ''}/api`;

// Resolve an image url which can be:
//   - absolute (http/https) => used as-is
//   - relative path starting with /api/... => prefix backend url
//   - relative path starting with /assets/... => used as-is (static build)
//   - empty => null
export const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  if (url.startsWith('/assets/')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('/')) return url;
  return url;
};

const SiteConfigContext = createContext(null);

export const SiteConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(STATIC_MODE ? staticConfig : null);
  const [loading, setLoading] = useState(!STATIC_MODE);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (STATIC_MODE) {
      setConfig(staticConfig);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/site`);
      setConfig(data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => ({ config, loading, error, reload: load, setConfig, staticMode: STATIC_MODE }), [config, loading, error, load]);
  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
};

export const useSiteConfig = () => {
  const ctx = useContext(SiteConfigContext);
  if (!ctx) throw new Error('useSiteConfig must be used inside SiteConfigProvider');
  return ctx;
};
